import { Injectable } from '@angular/core';
import { EmployeeGroupChangeRequestManagementControllerService } from '@iota/inh-core-v1-management-client';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, mergeMap, of, switchMap, take, withLatestFrom } from 'rxjs';
import {
    CancelEmployeeGroupChange,
    CancelEmployeeGroupChangeFailure,
    CancelEmployeeGroupChangeSuccess,
    LoadAllEmployeeGroupChangesByCompany,
    LoadAllEmployeeGroupChangesByCompanyFailure,
    LoadAllEmployeeGroupChangesByCompanySuccess,
    LoadCompanyEmployeeGroupChangeInformation,
    LoadCompanyEmployeeGroupChangeInformationFailure,
    LoadCompanyEmployeeGroupChangeInformationSuccess,
    ScheduleEmployeeGroupChange,
    ScheduleEmployeeGroupChangeFailure,
    ScheduleEmployeeGroupChangeSuccess,
    UpdateEmployeeGroupChangeDate,
    UpdateEmployeeGroupChangeDateFailure,
    UpdateEmployeeGroupChangeDateSuccess
} from '../actions/employee-group-change.actions';
import { companyEmployeeLastOverviewPageParams } from '../reducer/company-employee.reducer';
import { selectedCompanyId } from '../store';
import { toOverviewPageReload } from './employee-overview-reload';

@Injectable()
export class EmployeeGroupChangeEffects {
    loadCompanyEmployeeGroupChangeInformation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LoadCompanyEmployeeGroupChangeInformation),
            mergeMap(({ companyId }) =>
                this.api.getCompanyInformation(companyId).pipe(
                    take(1),
                    map((companyInformation) => LoadCompanyEmployeeGroupChangeInformationSuccess({ companyId, companyInformation })),
                    catchError((error) => of(LoadCompanyEmployeeGroupChangeInformationFailure({ error })))
                )
            )
        )
    );

    loadAllEmployeeGroupChangesByCompany$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LoadAllEmployeeGroupChangesByCompany),
            mergeMap(({ companyId }) =>
                this.api.getAllByCompany(companyId).pipe(
                    take(1),
                    map((employeeGroupChanges) => LoadAllEmployeeGroupChangesByCompanySuccess({ employeeGroupChanges })),
                    catchError((error) => of(LoadAllEmployeeGroupChangesByCompanyFailure({ error })))
                )
            )
        )
    );

    scheduleEmployeeGroupChange$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ScheduleEmployeeGroupChange),
            withLatestFrom(this.store.select(selectedCompanyId), this.store.select(companyEmployeeLastOverviewPageParams)),
            mergeMap(([{ request }, companyId, lastParams]) =>
                this.api.scheduleEmployeeGroupChange(request).pipe(
                    take(1),
                    switchMap((employeeGroupChangeRequest) => [
                        ScheduleEmployeeGroupChangeSuccess({ employeeGroupChangeRequest }),
                        ...toOverviewPageReload(companyId, lastParams)
                    ]),
                    catchError((error) => of(ScheduleEmployeeGroupChangeFailure({ error })))
                )
            )
        )
    );

    updateEmployeeGroupChangeDate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpdateEmployeeGroupChangeDate),
            withLatestFrom(this.store.select(selectedCompanyId), this.store.select(companyEmployeeLastOverviewPageParams)),
            mergeMap(([{ id, request }, companyId, lastParams]) =>
                this.api.updateEmployeeGroupChangeDate(id, request).pipe(
                    take(1),
                    switchMap((employeeGroupChangeRequest) => [
                        UpdateEmployeeGroupChangeDateSuccess({ employeeGroupChangeRequest }),
                        ...toOverviewPageReload(companyId, lastParams)
                    ]),
                    catchError((error) => of(UpdateEmployeeGroupChangeDateFailure({ error })))
                )
            )
        )
    );

    cancelEmployeeGroupChange$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CancelEmployeeGroupChange),
            withLatestFrom(this.store.select(selectedCompanyId), this.store.select(companyEmployeeLastOverviewPageParams)),
            mergeMap(([{ id }, companyId, lastParams]) =>
                this.api.cancelEmployeeGroupChange(id).pipe(
                    take(1),
                    switchMap((employeeGroupChangeRequest) => [
                        CancelEmployeeGroupChangeSuccess({ employeeGroupChangeRequest }),
                        ...toOverviewPageReload(companyId, lastParams)
                    ]),
                    catchError((error) => of(CancelEmployeeGroupChangeFailure({ error })))
                )
            )
        )
    );

    constructor(
        private actions$: Actions,
        private api: EmployeeGroupChangeRequestManagementControllerService,
        private store: Store
    ) {}
}
